import { Injectable } from "@angular/core";
import * as Sentry from "@sentry/angular";
import * as SentryElectron from "@sentry/electron/renderer";
import { environment } from "src/environments/environment";
import { isElectron } from "../utils/platform.utils";
import AppDetailsElectron from "src/app/plugins/app-details.plugin";

const messagesToIgnore = [
    //errors occuring on production
    'Invalid asset path',
    'Asset is already loaded',
    'Asset already exists',

    //errors occuring on only on staging
    'Failed to register a ServiceWorker',
    'webpack-dev-server',
    'Error: -10: Install Error(-10)',
    '-6: Install Error(-6)',
]

@Injectable({
    providedIn: "root",
})
export class SentryService {
    private isElectronPlatform = isElectron();

    constructor() { }

    async initSentry() {
        // Check if Electron app is packaged (production build)
        if (this.isElectronPlatform) {
            const isPackaged = await this.isElectronAppPackaged();
            if (!isPackaged) {
                console.log('[SentryService] Skipping Sentry init for Electron development mode');
                return;
            }
        }

        // Initialize Sentry based on platform
        if (this.isElectronPlatform) {
            this.initSentryElectron();
        } else {
            await this.initSentryWeb();
        }
    }

    /**
     * Initialize Sentry for Electron renderer process.
     * Uses @sentry/electron/renderer imported directly (not via contextBridge)
     * to avoid serialization issues with regexes and functions.
     * Passes @sentry/angular's init as the browser SDK so Electron can wrap it.
     */
    private initSentryElectron() {
        SentryElectron.init({
            dsn: environment.sentryDsnDesktop,
            ignoreErrors: messagesToIgnore,
            integrations: [
                SentryElectron.browserTracingIntegration(),
                SentryElectron.replayIntegration(),
                SentryElectron.captureConsoleIntegration({
                    levels: ['error']
                }),
                SentryElectron.feedbackIntegration({
                    autoInject: false,
                    colorScheme: 'system',
                    enableScreenshot: false,
                }),
            ],
            environment: environment.staging ? 'staging' : 'production',
            allowUrls: [
                "web.staging.playsopo.com",
                "app.playsopo.com",
                /^app:\/\//,
            ],
            tracesSampleRate: 1.0,
            tracePropagationTargets: [
                "localhost",
                /^https:\/\/sopo-development\.firebaseapp\.com/,
                /^https:\/\/sopo-master\.firebaseapp\.com/,
                /^https:\/\/api\.staging\.playsopo\.com/,
                /^https:\/\/api\.prod\.playsopo\.com/,
                /^https:\/\/gs\.staging\.playsopo\.com/,
                /^app:\/\//,
            ],
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
            maxBreadcrumbs: 2000,
        }, Sentry.init);

        console.debug('[SentryService] Sentry Electron initialized.');
    }

    /**
     * Initialize Sentry for web (browser) platform
     */
    private async initSentryWeb(): Promise<void> {
        const config = await this.buildWebConfig();
        Sentry.init(config);
        console.debug('[SentryService] Sentry Web initialized.');
    }

    /**
     * Build Sentry configuration for web (browser)
     */
    private async buildWebConfig(): Promise<Sentry.BrowserOptions> {
        // Load release version from build config
        let release: string | undefined;
        try {
            const response = await fetch('/build-config.json');
            if (response.ok) {
                const buildConfig = await response.json();
                release = buildConfig.release;
            }
        } catch (error) {
            console.warn('[SentryService] Failed to load build-config.json for release version');
        }

        return {
            dsn: environment.sentryDsnWeb,
            ignoreErrors: messagesToIgnore,
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration(),
                Sentry.captureConsoleIntegration({
                    levels: ['error']
                }),
                Sentry.feedbackIntegration({
                    autoInject: false,
                    colorScheme: 'system',
                    enableScreenshot: false,
                }),
            ],
            environment: environment.staging ? 'staging' : 'production',
            allowUrls: [
                'web.staging.playsopo.com',
                'app.playsopo.com',
            ],
            tracesSampleRate: 1.0,
            tracePropagationTargets: [
                "localhost",
                /^https:\/\/sopo-development\.firebaseapp\.com/,
                /^https:\/\/sopo-master\.firebaseapp\.com/,
                /^https:\/\/api\.staging\.playsopo\.com/,
                /^https:\/\/api\.prod\.playsopo\.com/,
                /^https:\/\/gs\.staging\.playsopo\.com/,
                /^https:\/\/gs\.prod\.playsopo\.com/,
            ],
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
            maxBreadcrumbs: 2000,
            release: release, // Critical: must match webpack upload release name
        };
    }

    /**
     * Check if Electron app is packaged (production build)
     */
    private async isElectronAppPackaged(): Promise<boolean> {
        try {
            const appInfo = await AppDetailsElectron.getAppInfo();
            return appInfo.isPackaged;
        } catch (error) {
            console.error('[SentryService] Failed to check if Electron is packaged:', error);
            return false;
        }
    }

    /**
     * Open Sentry feedback form for user bug reports
     */
    async sendBugReport(customContext: any) {
        const feedback = Sentry.getFeedback();

        if (!feedback) {
            console.error('[SentryService] Feedback integration not found. Sentry may not be initialized.');
            return;
        }

        const form = await feedback.createForm({
            tags: customContext,
            onSubmitError: (error: Error) => {
                console.error('[SentryService] - User feedback submit error', error);
            },
            onSubmitSuccess: () => {},
        });
        form.appendToDom();
        form.open();
    }

}
